import React, { useEffect, useState } from "react";
import { useFamilleConnectee } from "../lib/familleConnectee";
import { CLE_JETON } from "../espace/api";
import { Link, useSearchParams } from "react-router-dom";
import { Mail, Check, AlertTriangle, CircleCheck, Send } from "lucide-react";
import { PageDecor } from "../components/decor/PageDecor";
import { EmailSuggestion } from "../components/EmailSuggestion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// La session de l'espace famille, si la famille est connectée : elle prouve déjà l'adresse,
// inutile de lui envoyer un lien.
const enteteSession = () => {
  let jeton = "";
  try { jeton = localStorage.getItem(CLE_JETON) || ""; } catch { /* navigation privée */ }
  return jeton ? { Authorization: `Bearer ${jeton}` } : {};
};

const MOTIFS = [
  "Déménagement",
  "Horaire qui ne convient plus",
  "Raisons financières",
  "Blessure ou raison médicale",
  "Changement d'activité",
  "L'enfant ne s'y plaît plus",
  "Raisons familiales",
  "Insatisfaction (cours, professeur, organisation)",
  "Autre",
];

const Verdict = ({ v }) => {
  const ok = v.dans_les_delais;
  return (
    <div
      className="rounded-2xl p-5 mt-6"
      style={{
        background: ok ? "#ECFDF5" : "#FFFBEB",
        border: `1px solid ${ok ? "rgba(16,185,129,0.3)" : "rgba(245,158,11,0.35)"}`,
      }}
      data-testid="desinscription-verdict"
    >
      <div className="flex items-start gap-3">
        {ok
          ? <CircleCheck size={22} style={{ color: "#10B981" }} className="shrink-0 mt-0.5" />
          : <AlertTriangle size={22} style={{ color: "#F59E0B" }} className="shrink-0 mt-0.5" />}
        <div>
          <div className="font-semibold text-sm" style={{ color: ok ? "#065F46" : "#92400E" }}>
            {ok ? "Ta résiliation est dans les délais." : "Ta résiliation nous parvient après le délai."}
          </div>
          <p className="text-sm text-[var(--text)] mt-2 leading-relaxed">
            {ok ? (
              <>
                Les cours se poursuivent jusqu'au <strong>{v.fin_des_cours_fr}</strong>, fin du{" "}
                {v.semestre_du} — le dernier qui te sera facturé. Le {v.semestre_suivant}{" "}
                <strong>ne te sera pas facturé</strong>.
              </>
            ) : (
              <>
                Pour arrêter à la fin du semestre en cours, ta demande devait nous parvenir avant
                le <strong>{v.delai_manque_fr}</strong>. Conformément au règlement intérieur, la
                cotisation du <strong>{v.semestre_du}</strong> reste donc due — l'école confirmera
                le montant. Les cours se poursuivent jusqu'au{" "}
                <strong>{v.fin_des_cours_fr}</strong>, dernier jour de ce semestre.
              </>
            )}
          </p>
          <p className="text-xs text-[var(--muted)] mt-3 leading-relaxed">
            Jusqu'à cette date, l'élève continue de figurer dans les listes de cours et peut venir
            normalement : sa place ne se libère qu'à la fin du semestre dû.
          </p>
        </div>
      </div>
    </div>
  );
};

export default function Desinscription() {
  // Le lien personnel reçu par e-mail ouvre la page avec « ?jeton=… » : c'est lui qui
  // prouve que l'adresse est bien celle de la famille.
  const [params] = useSearchParams();
  const jeton = params.get("jeton") || "";
  const [email, setEmail] = useState("");
  const [lienEnvoye, setLienEnvoye] = useState(false);
  const [rienRecu, setRienRecu] = useState(false);
  const [data, setData] = useState(null);
  const [coches, setCoches] = useState({});
  const [motif, setMotif] = useState("");
  const [precision, setPrecision] = useState("");
  const [confirme, setConfirme] = useState(false);
  const [loading, setLoading] = useState(false);
  const [erreur, setErreur] = useState("");
  const [fini, setFini] = useState(null);
  const [aide, setAide] = useState({ prenom: "", nom: "", cours: "", motif: "" });
  const [aideEnvoye, setAideEnvoye] = useState(false);
  // Une famille connectée à son espace n'a rien à retaper : ses cours s'affichent
  // directement, elle coche simplement ceux qu'elle arrête.
  const famille = useFamilleConnectee();
  useEffect(() => {
    if (jeton) { charger(); return; }
    if (!famille?.email) return;
    setEmail(famille.email);
    charger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [famille, jeton]);

  // Les inscriptions de l'adresse prouvée (lien e-mail ou session de l'espace famille).
  const charger = async () => {
    setLoading(true);
    setErreur("");
    setData(null);
    try {
      const r = await fetch(`${API}/desinscription/inscriptions?jeton=${encodeURIComponent(jeton)}`, {
        headers: enteteSession(),
        credentials: "include",
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Recherche impossible");
      const d = await r.json();
      if (d.email) setEmail(d.email);
      setData(d);
    } catch (e) {
      setErreur(e.message);
    } finally {
      setLoading(false);
    }
  };

  const demanderLien = async () => {
    setLoading(true);
    setErreur("");
    try {
      const r = await fetch(`${API}/desinscription/lien`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, origine: window.location.origin }),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Envoi impossible");
      setLienEnvoye(true);
    } catch (e) {
      setErreur(e.message);
    } finally {
      setLoading(false);
    }
  };

  const actifs = (data?.eleves || []).flatMap((el) =>
    el.cours.filter((c) => !c.deja_desinscrit).map((c) => c.id));
  const selection = (data?.eleves || []).flatMap((el) =>
    el.cours.filter((c) => !c.deja_desinscrit && coches[c.id]).flatMap((c) => c.ids || [c.id]));
  const nbSelection = actifs.filter((id) => coches[id]).length;
  const toutSelectionne = actifs.length > 0 && nbSelection === actifs.length;
  // Adresse reconnue mais aucune ligne à arrêter (tout est déjà désinscrit) : sans porte de
  // sortie, la famille reste devant une liste grisée. Le formulaire de secours doit donc
  // s'ouvrir dans ce cas aussi, et pas seulement quand l'adresse est inconnue.
  const adresseInconnue = !!data && data.eleves.length === 0;
  const rienAArreter = !!data && actifs.length === 0;
  // Le formulaire de secours : rien à arrêter, ou aucun lien reçu (adresse peut-être
  // différente de celle de l'inscription).
  const secours = rienAArreter || (lienEnvoye && rienRecu);

  const insatisfaction = motif.startsWith("Insatisfaction");
  const manque = [];
  if (selection.length === 0) manque.push("cocher au moins un cours");
  if (!motif) manque.push("choisir le motif");
  if (!confirme) manque.push("cocher la case de confirmation");

  const valider = async () => {
    setLoading(true);
    setErreur("");
    try {
      const r = await fetch(`${API}/desinscription`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...enteteSession() },
        credentials: "include",
        body: JSON.stringify({ jeton, ids: selection, motif, precision }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.detail || "Enregistrement impossible");
      setFini(j);
    } catch (e) {
      setErreur(e.message);
    } finally {
      setLoading(false);
    }
  };

  const envoyerAide = async () => {
    setLoading(true);
    try {
      await fetch(`${API}/desinscription/aide`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...aide, email }),
      });
      setAideEnvoye(true);
    } finally {
      setLoading(false);
    }
  };

  if (fini) {
    return (
      <div data-testid="page-desinscription" className="pt-32 pb-24 relative">
        <PageDecor />
        <div className="max-w-2xl mx-auto px-6">
          <div className="rounded-3xl p-8" style={{ background: "#fff", border: "1.5px solid rgba(15,27,45,0.10)" }}>
            <Check size={28} style={{ color: "var(--primary)" }} />
            <h1 className="font-display text-3xl tracking-tight mt-4" data-testid="desinscription-fini">
              C'est enregistré
            </h1>
            <p className="text-sm text-[var(--muted)] mt-3 leading-relaxed">
              {fini.ecrites} cours désinscrit{fini.ecrites > 1 ? "s" : ""}. Un e-mail récapitulatif
              vient de t'être envoyé — conserve-le, il fait office de preuve.
            </p>
            <Verdict v={fini.verdict} />
            {fini.non_enregistres?.length > 0 && (
              <div
                className="rounded-2xl p-4 mt-4 text-sm leading-relaxed"
                style={{ background: "#FEF2F2", border: "1px solid rgba(220,38,38,0.25)", color: "#991B1B" }}
                data-testid="desinscription-partielle"
              >
                <strong>Attention :</strong> {fini.non_enregistres.join(", ")} n'a pas pu être
                enregistré (incident technique). L'école a été prévenue automatiquement et
                terminera à la main — inutile de recommencer, mais garde cet écran en mémoire.
              </div>
            )}
            <Link to="/" className="btn-primary mt-8 inline-flex" data-testid="desinscription-retour">
              Retour à l'accueil
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="page-desinscription" className="pt-32 pb-24 relative">
      <PageDecor />
      <div className="max-w-2xl mx-auto px-6">
        <div className="eyebrow mb-3">Arrêter les cours</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-[0.95]">
          Désinscription
        </h1>
        <p className="text-base text-[var(--muted)] mt-5 leading-relaxed">
          Indique ton adresse e-mail : nous t'envoyons un lien personnel qui ouvre les
          inscriptions de ta famille, et tu choisis les cours que tu souhaites arrêter.
        </p>
        <p className="text-sm text-[var(--muted)] mt-4 leading-relaxed" data-testid="desinscription-portee">
          Cette page ne concerne que les <strong>cours semestriels</strong>. Un cours d'essai
          s'annule ou se déplace avec le lien « Changer la date ou annuler » de son e-mail de
          confirmation. Pour un stage, une audition ou pour retirer une <strong>demande en liste
          d'attente</strong>, écris-nous simplement à{" "}
          <a href="mailto:info1000arts@gmail.com" className="underline hover:text-[var(--primary)]">
            info1000arts@gmail.com
          </a>.
        </p>

        {data?.verdict && (
          <div className="rounded-2xl p-5 mt-6 text-sm text-[var(--muted)] leading-relaxed"
               style={{ background: "var(--primary-tint)", border: "1px solid rgba(20,184,196,0.25)" }}>
            <strong className="text-[var(--text)]">La règle, avant de commencer.</strong> La
            résiliation doit nous parvenir au moins deux mois avant le début du semestre suivant :
            au plus tard le <strong>30 juin</strong> pour le semestre d'août à janvier, et le{" "}
            <strong>30 novembre</strong> pour celui de février à juin. Passé ce délai, la cotisation
            du semestre suivant reste due. Le détail de ta situation s'affiche plus bas.
          </div>
        )}

        {!data && !(jeton && loading) && (
        <div className="rounded-3xl p-6 mt-8" style={{ background: "#fff", border: "1.5px solid rgba(15,27,45,0.10)" }}>
          {lienEnvoye ? (
            <div data-testid="desinscription-lien-envoye">
              <Mail size={24} style={{ color: "var(--primary)" }} />
              <div className="font-display text-xl tracking-tight mt-3">Regarde ta boîte e-mail</div>
              <p className="text-sm text-[var(--muted)] mt-2 leading-relaxed">
                Si <strong>{email}</strong> est l'adresse d'une famille de l'école, on t'a envoyé un
                lien personnel (valable 2 heures). Ouvre-le pour choisir les cours à arrêter. Pense
                à regarder les indésirables.
              </p>
              <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4 text-sm">
                <button
                  type="button"
                  onClick={() => { setLienEnvoye(false); setRienRecu(false); }}
                  className="underline font-semibold"
                  data-testid="desinscription-autre-adresse"
                >
                  Essayer une autre adresse
                </button>
                {!rienRecu && (
                  <button
                    type="button"
                    onClick={() => setRienRecu(true)}
                    className="underline font-semibold"
                    data-testid="desinscription-rien-recu"
                  >
                    Je n'ai rien reçu
                  </button>
                )}
              </div>
            </div>
          ) : (
            <>
          <label className="text-sm font-medium">Adresse e-mail utilisée à l'inscription</label>
          <div className="flex flex-col sm:flex-row gap-3 mt-2">
            <input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setData(null); setCoches({}); }}
              onKeyDown={(e) => e.key === "Enter" && email && !loading && demanderLien()}
              placeholder="prenom.nom@exemple.ch"
              className="input-field flex-1"
              data-testid="desinscription-email"
            />
            <button
              type="button"
              onClick={() => demanderLien()}
              disabled={!email || loading}
              className="btn-primary justify-center"
              data-testid="desinscription-search"
            >
              <Mail size={16} /> {loading ? "Envoi…" : "Recevoir mon lien"}
            </button>
          </div>
          <EmailSuggestion value={email} onAccept={setEmail} testid="desinscription-email-suggestion" />
            </>
          )}
          {erreur && (
            <div className="text-sm mt-3" style={{ color: "#DC2626" }} data-testid="desinscription-erreur">
              {erreur}
            </div>
          )}
        </div>
        )}

        {data && data.eleves.length > 0 && (
          <div className="rounded-3xl p-6 mt-6" style={{ background: "#fff", border: "1.5px solid rgba(15,27,45,0.10)" }}
               data-testid="desinscription-liste">
            {email && (
              <div className="text-xs text-[var(--muted)] mb-2" data-testid="desinscription-adresse">
                Inscriptions rattachées à <strong>{email}</strong>
              </div>
            )}
            <div className="font-display text-xl tracking-tight">
              {rienAArreter
                ? "Les cours rattachés à cette adresse"
                : "Quels cours souhaites-tu arrêter\u00a0?"}
            </div>

            {!rienAArreter && (
              <label className="flex items-center gap-3 mt-5 text-sm font-medium cursor-pointer">
                <input
                  type="checkbox"
                  checked={toutSelectionne}
                  onChange={(e) => setCoches(e.target.checked
                    ? Object.fromEntries(actifs.map((id) => [id, true]))
                    : {})}
                  data-testid="desinscription-tout"
                />
                <span>Tout sélectionner (tous les élèves, tous les cours)</span>
              </label>
            )}

            {data.eleves.map((el) => (
              <div key={el.cle} className="mt-5 pt-5 border-t border-[var(--border)]">
                <div className="font-semibold">{el.prenom} {el.nom}</div>
                {el.cours.map((c) => (
                  <label
                    key={c.id}
                    className={`flex items-start gap-3 mt-3 text-sm ${c.deja_desinscrit ? "opacity-50" : "cursor-pointer"}`}
                  >
                    <input
                      type="checkbox"
                      disabled={c.deja_desinscrit}
                      checked={!!coches[c.id]}
                      onChange={(e) => setCoches((s) => ({ ...s, [c.id]: e.target.checked }))}
                      data-testid={`desinscription-cours-${c.id}`}
                      className="mt-1"
                    />
                    <span>
                      {c.cours}
                      {c.horaire && <span className="text-[var(--muted)]"> — {c.horaire}</span>}
                      {c.salle && <span className="text-[var(--muted)]"> · {c.salle}</span>}
                      {c.deja_desinscrit && (
                        <span className="block text-xs mt-0.5" style={{ color: "#92400E" }}>
                          Déjà enregistré ({c.statut}) — contacte-nous pour toute correction
                        </span>
                      )}
                    </span>
                  </label>
                ))}
              </div>
            ))}

            {nbSelection > 0 && <Verdict v={data.verdict} />}

            {toutSelectionne && (
              <div className="rounded-2xl p-4 mt-4 text-sm leading-relaxed"
                   style={{ background: "#FFF5F5", border: "1px solid rgba(227,6,19,0.25)", color: "#7F1D1D" }}
                   data-testid="desinscription-tout-avertissement">
                <strong>Tu quittes complètement l'école.</strong> Tous les cours de tous tes élèves
                sont sélectionnés. Si ce n'est pas ce que tu souhaites, décoche ceux que tu veux
                garder. Et si c'est un horaire qui ne convient plus, sache qu'un{" "}
                <Link to="/inscription" className="underline">changement de cours</Link> est souvent
                possible : écris-nous avant de tout arrêter.
              </div>
            )}

            {!rienAArreter && (
              <>
            <div className="mt-6">
              <label className="text-sm font-medium">Motif de la désinscription *</label>
              <select
                value={motif}
                onChange={(e) => setMotif(e.target.value)}
                className="input-field mt-2"
                data-testid="desinscription-motif"
              >
                <option value="">Choisir un motif…</option>
                {MOTIFS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              {(insatisfaction || motif === "Autre") && (
                <div className="mt-4" data-testid="desinscription-precision-bloc">
                  <label className="text-sm font-medium block">
                    {insatisfaction ? "Dis-nous en plus" : "Précise le motif"}
                    {insatisfaction && <span className="text-[var(--muted)] font-normal"> (facultatif)</span>}
                  </label>
                  <p className="text-xs text-[var(--muted)] mt-1 leading-relaxed">
                    {insatisfaction
                      ? "C'est la seule façon pour nous de nous améliorer. Ce message est lu par la direction, pas par le professeur concerné."
                      : "Quelques mots suffisent : cela nous aide à comprendre et à mieux accompagner les prochaines familles."}
                  </p>
                  <textarea
                    value={precision}
                    onChange={(e) => setPrecision(e.target.value.slice(0, 500))}
                    rows={4}
                    maxLength={500}
                    autoFocus
                    placeholder={insatisfaction
                      ? "Ex. : ma fille ne se sentait pas à l'aise dans le groupe, et les changements de professeur ont été difficiles à suivre."
                      : "Ex. : nous partons vivre à l'étranger en janvier."}
                    className="input-field mt-2 resize-y"
                    style={{ minHeight: 110, lineHeight: 1.6 }}
                    data-testid="desinscription-precision"
                  />
                  <div className="text-xs text-[var(--muted)] mt-1 text-right">
                    {precision.length}/500
                  </div>
                </div>
              )}
            </div>

            <label className="flex items-start gap-3 mt-5 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={confirme}
                onChange={(e) => setConfirme(e.target.checked)}
                className="mt-1"
                data-testid="desinscription-confirme"
              />
              <span>
                Je confirme la désinscription des cours cochés ci-dessus et j'ai pris connaissance
                des conditions de résiliation du{" "}
                <Link to="/reglement" className="underline">règlement intérieur</Link>.
              </span>
            </label>

            {manque.length > 0 && (
              <div className="rounded-2xl p-4 mt-5 text-sm"
                   style={{ background: "#FEF3C7", border: "1px solid #FCD34D", color: "#92400E" }}
                   data-testid="desinscription-manquants">
                <div className="font-semibold mb-2">Pour valider, il te reste à&nbsp;:</div>
                <ul className="space-y-1 list-disc pl-5">
                  {manque.map((m) => <li key={m}>{m}</li>)}
                </ul>
              </div>
            )}

            <button
              type="button"
              onClick={valider}
              disabled={manque.length > 0 || loading}
              className="btn-primary w-full justify-center mt-5"
              data-testid="desinscription-submit"
            >
              <Send size={16} /> {loading ? "Enregistrement…" : "Valider ma désinscription"}
            </button>
            {erreur && (
              <div
                className="rounded-2xl p-4 mt-4 text-sm leading-relaxed"
                style={{ background: "#FEF2F2", border: "1px solid rgba(220,38,38,0.3)", color: "#991B1B" }}
                data-testid="desinscription-erreur-validation"
              >
                {erreur}
              </div>
            )}
              </>
            )}
          </div>
        )}

        {secours && !aideEnvoye && (
          <div className="rounded-3xl p-6 mt-6" style={{ background: "#fff", border: "1.5px solid rgba(15,27,45,0.10)" }}
               data-testid="desinscription-introuvable">
            <div className="font-display text-xl tracking-tight">
              {!data
                ? "Tu n'as pas reçu de lien ?"
                : adresseInconnue
                  ? "Aucune inscription semestrielle sur cette adresse"
                  : "Il n'y a rien à arrêter pour cette adresse"}
            </div>
            {(!data || adresseInconnue) ? (
              <div className="rounded-2xl p-4 mt-3 text-sm leading-relaxed"
                   style={{ background: "#FEF3C7", border: "1px solid #FCD34D", color: "#92400E" }}
                   data-testid="desinscription-verifie-adresse">
                <strong>Vérifie d'abord l'orthographe</strong> : neuf fois sur dix, c'est une faute de
                frappe. Tu as saisi <strong>{email}</strong>. Utilise l'adresse exacte avec laquelle
                tu as inscrit ton enfant — celle sur laquelle tu as reçu la confirmation d'inscription.
                <button
                  type="button"
                  onClick={() => { setData(null); setAideEnvoye(false); setLienEnvoye(false); setRienRecu(false); }}
                  className="underline block mt-2 font-semibold"
                  data-testid="desinscription-reessayer"
                >
                  Réessayer avec une autre adresse
                </button>
              </div>
            ) : (
              <div className="rounded-2xl p-4 mt-3 text-sm leading-relaxed"
                   style={{ background: "#FEF3C7", border: "1px solid #FCD34D", color: "#92400E" }}
                   data-testid="desinscription-rien-a-arreter">
                Chaque cours rattaché à <strong>{email}</strong> porte déjà un statut : il n'y a donc
                rien à valider ici, et rien ne sera facturé de plus de ce fait. Si tu voulais arrêter
                un autre cours, ou si un statut te semble faux, remplis le formulaire ci-dessous —
                l'école le traite à la main.
                <button
                  type="button"
                  onClick={() => { setData(null); setAideEnvoye(false); }}
                  className="underline block mt-2 font-semibold"
                  data-testid="desinscription-reessayer"
                >
                  Essayer avec une autre adresse
                </button>
              </div>
            )}
            <p className="text-sm text-[var(--muted)] mt-4 leading-relaxed">
              {(!data || adresseInconnue)
                ? "Si l'adresse est la bonne, l'inscription a peut-être été faite avec une autre : indique le prénom, le nom de l'élève et le cours concerné, l'école s'en occupe à la main. Rien n'est enregistré automatiquement."
                : "Indique le prénom, le nom de l'élève et le cours concerné : l'école s'en occupe à la main. Rien n'est enregistré automatiquement, mais la date d'aujourd'hui fait foi pour le délai."}
            </p>
            <div className="grid sm:grid-cols-2 gap-3 mt-4">
              <input placeholder="Prénom de l'élève" value={aide.prenom}
                     onChange={(e) => setAide({ ...aide, prenom: e.target.value })}
                     className="input-field" data-testid="desinscription-aide-prenom" />
              <input placeholder="Nom de l'élève" value={aide.nom}
                     onChange={(e) => setAide({ ...aide, nom: e.target.value })}
                     className="input-field" data-testid="desinscription-aide-nom" />
            </div>
            <input placeholder="Cours à arrêter (ex. Hip-hop mercredi 15h)" value={aide.cours}
                   onChange={(e) => setAide({ ...aide, cours: e.target.value })}
                   className="input-field mt-3" data-testid="desinscription-aide-cours" />
            <select value={aide.motif} onChange={(e) => setAide({ ...aide, motif: e.target.value })}
                    className="input-field mt-3" data-testid="desinscription-aide-motif">
              <option value="">Motif…</option>
              {MOTIFS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
            <button
              type="button"
              onClick={envoyerAide}
              disabled={!aide.prenom || !aide.nom || !aide.cours || !aide.motif || loading}
              className="btn-primary w-full justify-center mt-4"
              data-testid="desinscription-aide-submit"
            >
              <Send size={16} /> Envoyer ma demande à l'école
            </button>
          </div>
        )}

        {aideEnvoye && (
          <div className="rounded-3xl p-6 mt-6" style={{ background: "#fff", border: "1.5px solid rgba(15,27,45,0.10)" }}
               data-testid="desinscription-aide-envoye">
            <Check size={24} style={{ color: "var(--primary)" }} />
            <div className="font-display text-xl tracking-tight mt-3">Demande transmise</div>
            <p className="text-sm text-[var(--muted)] mt-2 leading-relaxed">
              L'école a reçu ta demande et te répondra. La date d'aujourd'hui fait foi pour le délai
              de résiliation.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
